import React, { useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { EditorToolbar } from './EditorToolbar';
import { PasteImageUpload } from './PasteImageUpload';
import { AffiliateCtaExtension } from './AffiliateCtaExtension';
import { uploaderUtils } from '../image-uploader/uploaderUtils';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import './editor.css';
import { cn } from '@/lib/utils';

export interface RichTextEditorProps {
  content?: string;  // Changed from value to content to match usage
  value?: string;    // Added backward compatibility
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: number;
}

export const RichTextEditor = ({
  content,
  value,
  onChange,
  placeholder = 'Start writing...',
  className,
  minHeight = 200,
}: RichTextEditorProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const { user } = useAuth();
  
  // Use content prop or fall back to value prop for backward compatibility
  const editorContent = content || value || '';

  const handleImageUpload = async (file: File): Promise<string> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      const url = await uploaderUtils.uploadToSupabase(file, user.id);
      toast({
        title: 'Image uploaded',
        description: 'Your image has been successfully uploaded.',
      });
      return url;
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'mx-auto max-w-full h-auto rounded-md',
        },
      }),
      PasteImageUpload.configure({
        uploadFn: handleImageUpload,
        onError: (error) => {
          console.error('Paste image upload error:', error);
          toast({
            title: 'Upload failed',
            description: 'Failed to upload pasted image. Please try again.',
            variant: 'destructive',
          });
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'mx-auto table-auto border-collapse border border-border my-4',
        },
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border-b border-border',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-border bg-muted p-2 text-left font-semibold',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-border p-2',
        },
      }),
      AffiliateCtaExtension,
    ],
    content: editorContent,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onFocus: () => {
      setIsFocused(true);
    },
    onBlur: () => {
      setIsFocused(false);
    },
  });

  return (
    <div 
      className={cn(
        "border rounded-md overflow-hidden", 
        isFocused ? "ring-2 ring-ring ring-offset-1" : "",
        className
      )}
    >
      <EditorToolbar editor={editor} />
      <div 
        className="p-3 prose prose-sm max-w-none"
        style={{ minHeight: `${minHeight}px` }}
      >
        <EditorContent editor={editor} className="outline-none" />
      </div>
    </div>
  );
};
